import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const SERVICES = ["Legal", "Capital", "Build", "Distribution", "Mentorship", "Operations"];

export default function RequestMatchDialog({ ideaId, trigger }: { ideaId: string; trigger?: React.ReactNode }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [services, setServices] = useState<string[]>([]);
  const [counties, setCounties] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggle = (s: string) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async () => {
    if (!user) return;
    if (services.length === 0) {
      toast.error("Pick at least one service category.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("idea_match_requests").insert({
      idea_id: ideaId,
      requested_by_user_id: user.id,
      services_needed: services,
      counties: counties.split(",").map((c) => c.trim()).filter(Boolean),
      notes: notes.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Could not submit request", { description: error.message });
      return;
    }
    toast.success("Match request submitted. We'll route it to qualified partners.");
    setOpen(false);
    setServices([]);
    setCounties("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Find a Partner
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request a partner match</DialogTitle>
          <DialogDescription>
            Tell us what kind of help you need. We'll route qualified partners to you — you stay in control of who you accept.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Services needed</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {SERVICES.map((s) => {
                const active = services.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggle(s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label htmlFor="rmc">Counties (comma-separated, optional)</Label>
            <Input id="rmc" value={counties} onChange={(e) => setCounties(e.target.value)} placeholder="Mobile, Baldwin" className="mt-1.5" />
          </div>

          <div>
            <Label htmlFor="rmn">Notes for partners</Label>
            <Textarea
              id="rmn"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Scope, timeline, budget, constraints…"
              className="mt-1.5 min-h-[100px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>{submitting ? "Submitting…" : "Request match"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}